import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getDefaultRoute } from '../utils/defaultRoute';
import { identifyAirbridgeUser, trackSignin } from '../utils/airbridge';
import { persistAuthSession } from '../utils/authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Handles the redirect back from Emergent-managed Google OAuth.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleAuthCallback({ onLogin }) {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const urlParams = new URLSearchParams(window.location.search);

    // cap_session is present when this callback is reached from the Capacitor WebView flow.
    // In that case, we park the JWT so the app can pick it up via polling.
    const capSession = urlParams.get('cap_session');

    // ── Direct Google OAuth flow (implicit, response_type=token) ──────────────
    // When we redirect straight to accounts.google.com with the user's own client ID,
    // Google returns access_token in the URL fragment. The cap_session is in `state`.
    const accessTokenMatch = hash.match(/[#&]access_token=([^&]+)/);
    if (accessTokenMatch) {
      const accessToken = decodeURIComponent(accessTokenMatch[1]);
      const stateMatch = hash.match(/[#&]state=([^&]+)/);
      const stateStr = stateMatch ? decodeURIComponent(stateMatch[1]) : '';
      // state is encoded as "cap=<cap_session>"
      const capFromState = stateStr.startsWith('cap=') ? decodeURIComponent(stateStr.slice(4)) : null;
      const effectiveCapSession = capFromState || capSession;

      (async () => {
        try {
          const res = await axios.post(`${API}/auth/google/oauth-token`, { access_token: accessToken });

          if (res.data.needs_profile) {
            navigate('/google/complete', {
              replace: true,
              state: {
                signupToken: res.data.signup_token,
                email: res.data.email,
                suggestedFirstName: res.data.suggested_first_name || '',
                suggestedLastName: res.data.suggested_last_name || '',
                capSession: effectiveCapSession,
              },
            });
            return;
          }

          if (effectiveCapSession) {
            await axios.post(`${API}/auth/park-app-token`, {
              session_id: effectiveCapSession,
              access_token: res.data.access_token,
            });
            navigate('/app-login-complete', { replace: true });
            return;
          }

          // Standard web flow (no cap_session)
          await persistAuthSession(res.data.access_token, res.data.user);
          onLogin?.(res.data.user);
          identifyAirbridgeUser(res.data.user);
          trackSignin({ user: res.data.user, method: 'google' });
          toast.success('Welcome back!');
          navigate(getDefaultRoute(res.data.user), { replace: true });
        } catch (err) {
          const detail = err.response?.data?.detail;
          toast.error(typeof detail === 'string' ? detail : 'Google sign-in failed.');
          navigate('/login', { replace: true });
        }
      })();
      return;
    }

    // ── Emergent-managed OAuth flow (session_id in hash) ─────────────────────
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : '';

    if (!sessionId) {
      toast.error('Google sign-in was cancelled.');
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        if (res.data.needs_profile) {
          navigate('/google/complete', {
            replace: true,
            state: {
              signupToken: res.data.signup_token,
              email: res.data.email,
              suggestedFirstName: res.data.suggested_first_name || '',
              suggestedLastName: res.data.suggested_last_name || '',
              capSession,
            },
          });
          return;
        }

        // Capacitor flow: park the token for the app to pick up, then show /app-login-complete
        if (capSession) {
          await axios.post(`${API}/auth/park-app-token`, {
            session_id: capSession,
            access_token: res.data.access_token,
          });
          navigate('/app-login-complete', { replace: true });
          return;
        }

        // Standard web flow
        await persistAuthSession(res.data.access_token, res.data.user);
        onLogin?.(res.data.user);
        identifyAirbridgeUser(res.data.user);
        trackSignin({ user: res.data.user, method: 'google' });
        toast.success('Welcome back!');
        navigate(getDefaultRoute(res.data.user), { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Google sign-in failed.');
        navigate('/login', { replace: true });
      }
    })();
  }, [navigate, onLogin]);

  return (
    <div
      className="app-shell"
      data-testid="google-auth-callback"
      style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--app-bg)' }}
    >
      <img src="/allure-logo.png" alt="Allure" style={{ width: 80, height: 80, objectFit: 'contain' }} className="animate-pulse" />
    </div>
  );
}
